import React from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { StatusBadge } from '@/components/StatusBadge';
import { ImagePreview } from '@/components/ImagePreview';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Eye,
  IndianRupee,
} from 'lucide-react';
import type { PaymentMemberRow } from '@/lib/payments';
import { formatCurrency, formatPaymentDate } from '@/lib/payments';
import {
  getLifeStatus,
  getPaymentMonthStatus,
  isAddPaymentDisabled,
} from '@/lib/utils';

export type SortField =
  | 'id'
  | 'name'
  | 'totalPaid'
  | 'amountDue'
  | 'monthsPaid'
  | 'lastPaymentDate'
  | 'status';

export type SortDirection = 'asc' | 'desc';

export interface ColumnSort {
  field: SortField;
  direction: SortDirection;
}

export interface TableColumn {
  id: string;
  label: string;
  sortable: boolean;
  renderHeader: (sort: ColumnSort | null, onSort: (field: SortField) => void) => React.ReactNode;
  renderCell: (row: PaymentMemberRow) => React.ReactNode;
  className?: string;
}

const ACTION_BUTTON_CLASS =
  'h-8 w-8 rounded-md border border-gray-200 text-gray-500 hover:border-[#4369a0]/30 hover:bg-[#4369a0]/10 hover:text-[#4369a0]';

const HeaderLabel: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className,
}) => (
  <span
    className={`text-xs font-semibold tracking-wide text-gray-800 ${className ?? ''}`}
  >
    {children}
  </span>
);

const SortButton: React.FC<{
  field: SortField;
  currentSort: ColumnSort | null;
  onSort: (field: SortField) => void;
  children: React.ReactNode;
  className?: string;
}> = ({ field, currentSort, onSort, children, className }) => {
  const isActive = currentSort?.field === field;
  const isAsc = currentSort?.direction === 'asc';

  return (
    <Button
      variant="ghost"
      size="sm"
      className={`-ml-2 h-8 gap-1 px-2 text-xs font-semibold tracking-wide hover:bg-gray-100 ${
        isActive ? 'text-gray-900' : 'text-gray-800 hover:text-gray-900'
      } ${className ?? ''}`}
      onClick={() => onSort(field)}
    >
      {children}
      {isActive ? (
        isAsc ? (
          <ArrowUp className="h-3.5 w-3.5 text-[#4369a0]" />
        ) : (
          <ArrowDown className="h-3.5 w-3.5 text-[#4369a0]" />
        )
      ) : (
        <ArrowUpDown className="h-3.5 w-3.5 text-gray-400" />
      )}
    </Button>
  );
};

export const createColumns = (
  sort: ColumnSort | null,
  onSort: (field: SortField) => void,
  onView: (row: PaymentMemberRow) => void,
  onAddPayment: (row: PaymentMemberRow) => void
): TableColumn[] => [
  {
    id: 'id',
    label: 'ID',
    sortable: true,
    className: 'w-[72px] text-center',
    renderHeader: () => (
      <div className="flex items-center justify-center">
        <SortButton field="id" currentSort={sort} onSort={onSort} className="mx-auto">
          ID
        </SortButton>
      </div>
    ),
    renderCell: (row) => (
      <div className="text-center text-sm font-medium tabular-nums text-gray-500">
        {row.member.id}
      </div>
    ),
  },
  {
    id: 'member',
    label: 'Member',
    sortable: true,
    className: 'w-[240px] max-w-[260px] pr-2',
    renderHeader: () => (
      <SortButton field="name" currentSort={sort} onSort={onSort}>
        Member
      </SortButton>
    ),
    renderCell: (row) => (
      <div className="flex min-w-0 items-center gap-2.5">
        <ImagePreview src={row.member.profileImage || row.member.photo} alt={row.member.name || 'Member photo'}>
        <Avatar className="h-9 w-9 shrink-0 border border-white shadow-sm ring-1 ring-gray-200/60">
          <AvatarImage src={row.member.profileImage || row.member.photo} />
          <AvatarFallback className="bg-[#4369a0]/10 text-xs font-semibold text-[#4369a0]">
            {row.member.name
              .split(' ')
              .map((n) => n[0])
              .join('')
              .toUpperCase()}
          </AvatarFallback>
        </Avatar>
        </ImagePreview>
        <div className="min-w-0 flex-1">
          <button
            type="button"
            onClick={() => onView(row)}
            className="block w-full cursor-pointer truncate text-left text-sm font-medium text-gray-900 hover:text-[#4369a0] hover:underline"
          >
            {row.member.name}
          </button>
          <div className="truncate text-xs text-gray-500">+91 {row.member.phone}</div>
        </div>
      </div>
    ),
  },
  {
    id: 'lifeStatus',
    label: 'Life Status',
    sortable: false,
    className: 'w-[110px]',
    renderHeader: () => <HeaderLabel>Life Status</HeaderLabel>,
    renderCell: (row) => {
      return <StatusBadge status={getLifeStatus(row.member)} />;
    },
  },
  {
    id: 'status',
    label: 'This Month',
    sortable: true,
    className: 'w-[120px]',
    renderHeader: () => (
      <SortButton field="status" currentSort={sort} onSort={onSort}>
        This Month
      </SortButton>
    ),
    renderCell: (row) => {
      return (
        <StatusBadge
          status={getPaymentMonthStatus(
            row.member,
            row.isPaidThisMonth,
            row.summary.monthsPaid
          )}
        />
      );
    },
  },
  {
    id: 'totalPaid',
    label: 'Total Paid',
    sortable: true,
    className: 'w-[110px] whitespace-nowrap',
    renderHeader: () => (
      <SortButton field="totalPaid" currentSort={sort} onSort={onSort}>
        Total Paid
      </SortButton>
    ),
    renderCell: (row) => (
      <div className="text-sm font-medium tabular-nums text-teal-800">
        {formatCurrency(row.summary.totalPaid)}
      </div>
    ),
  },
  {
    id: 'amountDue',
    label: 'Due',
    sortable: true,
    className: 'w-[100px] whitespace-nowrap',
    renderHeader: () => (
      <SortButton field="amountDue" currentSort={sort} onSort={onSort}>
        Due
      </SortButton>
    ),
    renderCell: (row) => (
      <div className="text-sm font-medium tabular-nums text-gray-700">
        {formatCurrency(row.summary.amountDue)}
      </div>
    ),
  },
  {
    id: 'monthsPaid',
    label: 'Progress',
    sortable: true,
    className: 'w-[120px]',
    renderHeader: () => (
      <SortButton field="monthsPaid" currentSort={sort} onSort={onSort}>
        Progress
      </SortButton>
    ),
    renderCell: (row) => (
      <div className="space-y-1">
        <div className="text-xs font-medium tabular-nums text-gray-600">
          {row.summary.monthsPaid} / 100
        </div>
        <div className="h-1.5 overflow-hidden rounded-full bg-gray-100">
          <div
            className="h-full rounded-full bg-[#4369a0] transition-all"
            style={{ width: `${row.summary.paymentProgress}%` }}
          />
        </div>
      </div>
    ),
  },
  {
    id: 'lastPayment',
    label: 'Last Payment',
    sortable: true,
    className: 'min-w-[130px]',
    renderHeader: () => (
      <SortButton field="lastPaymentDate" currentSort={sort} onSort={onSort}>
        Last Payment
      </SortButton>
    ),
    renderCell: (row) =>
      row.lastPaymentDate ? (
        <div>
          <div className="text-sm text-gray-700">{formatPaymentDate(row.lastPaymentDate)}</div>
          {row.lastPaymentType && (
            <StatusBadge status={row.lastPaymentType} />
          )}
        </div>
      ) : (
        <div className="text-sm text-gray-400">--</div>
      ),
  },
  {
    id: 'actions',
    label: 'Actions',
    sortable: false,
    className: 'w-[108px] text-center',
    renderHeader: () => <HeaderLabel className="block text-center">Actions</HeaderLabel>,
    renderCell: (row) => {
      const paymentStatus = getPaymentMonthStatus(
        row.member,
        row.isPaidThisMonth,
        row.summary.monthsPaid
      );
      const addPaymentDisabled = isAddPaymentDisabled(row.member, row.summary.monthsPaid);

      return (
      <div className="flex items-center justify-center gap-1.5">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onView(row)}
              className={ACTION_BUTTON_CLASS}
            >
              <Eye className="h-3.5 w-3.5" />
              <span className="sr-only">View payments</span>
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom">View</TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onAddPayment(row)}
              disabled={addPaymentDisabled}
              className={ACTION_BUTTON_CLASS}
            >
              <IndianRupee className="h-3.5 w-3.5" />
              <span className="sr-only">Add payment</span>
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom">
            {addPaymentDisabled
              ? paymentStatus === 'no_dues'
                ? 'No dues'
                : 'Account closed'
              : 'Add payment'}
          </TooltipContent>
        </Tooltip>
      </div>
      );
    },
  },
];
